
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, reactive, nextTick, PropType } from 'vue'
import * as echarts from 'echarts'
import { useStarTheme } from '@/utils/useStarTheme'
import { useStarRouter } from '@/utils/useStarTheme'
import { formatSquareUnlockTag } from '@/utils/starZoneHelper'

export interface ChartPoint {
  date: string
  strategy: number
  benchmark: number
  eventType?: 'subscribe' | 'follow' | 'interrupted'
}

export interface StrategyItem {
  id: string | number
  name: string
  type: 'stock' | 'group'
  followStatus?: 'following' | 'interrupted' | null
  subscribeStatus?: 'following' | 'interrupted' | 'subscribed' | null
  strategyReturn: number | null
  annRet?: number | null
  maxDrawdown: number | null
  profitLossRatio: number | null
  chartData: ChartPoint[]
  quantType?: number
  typeName?: string
  rightTag?: { content: string; url: string }
  /** 模版策略：所需星级 */
  starLevel?: number
  starTag?: string
  /** 模版策略：接口 hasPermission，false 时展示解锁标识 */
  hasPermission?: boolean
  leftTags?: { content: string }[]
  winRate?: number,
  riskRatio :number
  noBuySell?: boolean
}

export default defineComponent({
  name: 'StrategyCard',
  props: {
    item: { type: Object as PropType<StrategyItem>, required: true },
    /** home: 我的策略；square: 模版策略 */
    source: { type: String as PropType<'home' | 'square'>, default: 'home' },
  },
  emits: ['follow', 'live'],
  setup(props, { emit }) {
    const { colors, isLight } = useStarTheme()
    const { starPush } = useStarRouter()

    const squareUnlockBadge = computed(() => {
      if (props.source === 'square') {
        if (props.item.hasPermission === true) return undefined
        const content = formatSquareUnlockTag(props.item.starLevel, props.item.starTag)
        return content ? { content, url: '' } : undefined
      }
      return props.item.rightTag
    })

    const cardStyle = computed(() => ({
      background: isLight.value
        ? 'transparent'
        : 'linear-gradient(180deg, #252525 0%, #262626 100%)',
      border: isLight.value ? '1px solid #dbdfe6' : 'none',
      boxShadow: isLight.value ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
    }))
    const chartRef = ref<HTMLElement | null>(null)
    let chartInstance: echarts.ECharts | null = null

    const tooltip = reactive({
      visible: false,
      x: 0,
      y: 0,
      date: '',
      eventType: null as string | null,
      strategyVal: '',
      benchmarkVal: ''
    })

    const hasChartData = computed(() => (props.item.chartData?.length ?? 0) > 0)

    const isFollowing = computed(() =>
      props.item.followStatus === 'following' || props.item.followStatus === 'interrupted'
    )

    /** 与 tag.content、回测记录/消息保存（taskID 含 _com_save）一致：组合 → group/result，个股 → stock/result */
    const isGroupStrategy = computed(() => {
      const tags = props.item.leftTags || []
      if (tags.some((t) => t.content === '组合量化')) return true
      if (tags.some((t) => t.content === '个股量化')) return false
      const taskId = String(props.item.id || '')
      if (taskId.includes('_com_save')) return true
      const qt = props.item.quantType
      if (qt === 3 || qt === 4) return true
      return props.item.type === 'group'
    })

    const STRATEGY_LINE_COLOR = '#e8392a'

    const returnColor = computed(() => {
      const v = props.item.strategyReturn
      if (v == null || isNaN(v)) return '#999999'
      return v > 0 ? '#e8392a' : v < 0 ? '#26a17b' : '#999999'
    })

    const eventTypeLabel = (type: string) => {
      const map: Record<string, string> = { subscribe: '订阅', follow: '跟投', interrupted: '中断' }
      return map[type] || ''
    }

    const formatDate = (val: string | number) => {
      const d = new Date(Number(val))
      if (isNaN(d.getTime())) return String(val)
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}${m}${day}`
    }

    const formatReturn = (val: number | null | undefined) => {
      if (val == null || isNaN(val)) return '--'
      const sign = val > 0 ? '+' : ''
      return `${sign}${val.toFixed(2)}%`
    }

    const initChart = () => {
      if (!hasChartData.value || !chartRef.value) return
      chartInstance = echarts.init(chartRef.value, isLight.value ? 'light' : 'dark')

      const data = props.item.chartData
      const dates = data.map(d => d.date)
      const strategyVals = data.map(d => d.strategy)
      const benchmarkVals = data.map(d => d.benchmark)

      // 事件标记点
      const markPoints = data
        .filter(d => d.eventType)
        .map(d => {
          const colorMap: Record<string, string> = {
            subscribe: '#f5a623',
            follow: '#26a17b',
            interrupted: '#999999'
          }
          return {
            name: d.eventType!,
            coord: [d.date, d.strategy] as [string, number],
            symbol: 'circle',
            symbolSize: 8,
            itemStyle: { color: colorMap[d.eventType!] }
          }
        })

      const sColor = STRATEGY_LINE_COLOR
      // 阴影始终在折线下方，从线条颜色渐变到透明
      const areaGradient = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: sColor + '55' },
        { offset: 1, color: sColor + '00' }
      ])
      const option: echarts.EChartsOption = {
        backgroundColor: 'transparent',
        grid: { top: 4, bottom: 4, left: 4, right: 4, containLabel: false },
        xAxis: { type: 'category', data: dates, show: false },
        yAxis: { type: 'value', show: false },
        series: [
          {
            type: 'line',
            data: strategyVals,
            smooth: true,
            symbol: 'none',
            lineStyle: { color: sColor, width: 1.5 },
            areaStyle: {
              origin: 'start',
              color: areaGradient
            },
            markPoint: { data: markPoints, label: { show: false } }
          },
          {
            type: 'line',
            data: benchmarkVals,
            smooth: true,
            symbol: 'none',
            lineStyle: { color: '#999999', width: 1.5 },
            areaStyle: { color: 'transparent' }
          }
        ],
        tooltip: { show: false } // 用自定义浮窗
      }

      chartInstance.setOption(option)

      // echarts 事件已移除，改用 Vue @mousemove 绑定在图表 div 上
    }

    const onChartMouseMove = (e: MouseEvent) => {
      if (!hasChartData.value || !chartInstance || !chartRef.value) {
        console.log('[tooltip] no chartInstance or chartRef')
        return
      }
      const rect = chartRef.value.getBoundingClientRect()
      const offsetX = e.clientX - rect.left
      const offsetY = e.clientY - rect.top
      const pt = chartInstance.convertFromPixel({ seriesIndex: 0 }, [offsetX, offsetY])
      console.log('[tooltip] offsetX:', offsetX, 'offsetY:', offsetY, 'pt:', pt)
      if (!pt || pt[0] == null) { tooltip.visible = false; return }
      const data = props.item.chartData
      const idx = Math.max(0, Math.min(Math.round(pt[0]), data.length - 1))
      const d = data[idx]
      console.log('[tooltip] idx:', idx, 'd:', d)
      if (!d) { tooltip.visible = false; return }
      tooltip.visible = true
      tooltip.x = e.clientX + 12
      tooltip.y = e.clientY - 10
      tooltip.date = formatDate(d.date)
      tooltip.eventType = d.eventType || null
      tooltip.strategyVal = formatReturn(d.strategy)
      tooltip.benchmarkVal = formatReturn(d.benchmark)
      console.log('[tooltip] visible:', tooltip.visible, 'x:', tooltip.x, 'y:', tooltip.y)
    }

    const handleBtnClick = () => {
      const extraQuery: Record<string, string> = {
        taskID: String(props.item.id),
        isDetails: 'true',
        isfrom: '2',
      }
      if (props.source === 'square') {
        extraQuery.isFactor = '1'
        extraQuery.hasPermission = props.item.hasPermission === true ? '1' : '0'
        starPush('/star/square/result', extraQuery)
        return
      }
      if (isGroupStrategy.value) {
        if (props.item.quantType === 4) {
          extraQuery.isFactor = '1'
        }
        starPush('/star/group/result', extraQuery)
      } else {
        const qt = props.item.quantType
        starPush('/star/stock/result', { ...extraQuery, quantType: String(qt === 2 ? 2 : 1) })
      }
    }
const formatNumber = (val: number | undefined, decimals: number) => {
  if (val === undefined || val == null) return '--'
  const factor = Math.pow(10, decimals)
  const truncated = Math.trunc(val * factor) / factor
  return truncated.toFixed(decimals)
}
    onMounted(() => nextTick(() => {
      if (hasChartData.value) initChart()
    }))
    onBeforeUnmount(() => chartInstance?.dispose())

    return {
      colors, isLight, chartRef, tooltip, isFollowing, hasChartData,
      STRATEGY_LINE_COLOR, returnColor, cardStyle, squareUnlockBadge,
      formatReturn, eventTypeLabel, isGroupStrategy, handleBtnClick,
      onChartMouseMove,
      formatNumber
    }
  }
})
